import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Space,
  Tag,
  Descriptions,
  Typography,
  Divider,
  Timeline,
  Input,
  Popconfirm,
  Alert,
  Table,
  Tooltip,
  Row,
  Col,
  Card,
  Statistic,
  Badge,
  Progress,
  Spin,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  FileDoneOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  PaperClipOutlined,
  CheckOutlined,
  StopOutlined,
  QuestionCircleOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  verifyBillRequest,
  rejectBillRequest,
  closeBillRequest,
  fetchPOReconciliationRequest,
  clearBillError,
} from '../../redux/actions/billReceiptActions';
import { hasPermission } from '../../utils/permissions';
import VerificationErrorAlert from './VerificationErrorAlert';
import PreVerificationCheck from './PreVerificationCheck';
import StrictVerificationInfo from './StrictVerificationInfo';
import PaymentPanel from '../Payment/PaymentPanel';
import { intelligenceAPI } from '../../api/config';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

// ─────────────────────────────────────────────────────────────────────────────
// Formatters
// ─────────────────────────────────────────────────────────────────────────────

const fmtDate = (val) =>
  val ? dayjs(val).format('DD MMM YYYY, hh:mm A') : '—';

const fmtShortDate = (val) =>
  val ? dayjs(val).format('DD MMM YYYY') : '—';

const fmtNum = (val, dec = 2) =>
  val != null
    ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec })
    : '—';

const fmtMoney = (val) =>
  val != null ? `₹ ${fmtNum(val)}` : '—';

// Compute derived amounts from billed fields (mirrors server-side logic)
const computeAmounts = (qty, rate, discPct, taxSlab) => {
  const q = parseFloat(qty) || 0;
  const r = parseFloat(rate) || 0;
  const d = parseFloat(discPct) || 0;
  const t = parseFloat(taxSlab) || 0;
  const subtotal    = q * r;
  const discountAmt = (subtotal * d) / 100;
  const taxable     = subtotal - discountAmt;
  const taxAmt      = (taxable * t) / 100;
  const total       = taxable + taxAmt;
  return { subtotal, discountAmt, taxable, taxAmt, total };
};

// ─────────────────────────────────────────────────────────────────────────────
// Shared status tag helpers (exported for BillReceipt.js)
// ─────────────────────────────────────────────────────────────────────────────

export const getBillStatusTag = (status) => {
  const map = {
    received: { color: 'gold',    icon: <ClockCircleOutlined />,  text: 'Received' },
    verified: { color: 'blue',    icon: <CheckCircleOutlined />,  text: 'Verified' },
    closed:   { color: 'green',   icon: <FileDoneOutlined />,     text: 'Closed'   },
    rejected: { color: 'red',     icon: <CloseCircleOutlined />,  text: 'Rejected' },
  };
  const cfg = map[status] || { color: 'default', icon: null, text: status || '—' };
  return (
    <Tag color={cfg.color} icon={cfg.icon} style={{ fontWeight: 500 }}>
      {cfg.text}
    </Tag>
  );
};

export const getReconStatusTag = (status) => {
  const map = {
    matched:    { color: 'success', icon: <CheckCircleOutlined />,       text: 'Matched'    },
    mismatched: { color: 'error',   icon: <ExclamationCircleOutlined />, text: 'Mismatched' },
    pending:    { color: 'warning', icon: <ClockCircleOutlined />,       text: 'Pending'    },
  };
  const cfg = map[status] || { color: 'default', icon: null, text: status || '—' };
  return (
    <Tag color={cfg.color} icon={cfg.icon} style={{ fontWeight: 500 }}>
      {cfg.text}
    </Tag>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Section heading helper
// ─────────────────────────────────────────────────────────────────────────────

const SectionTitle = ({ children }) => (
  <div className="bill-detail-section-title">{children}</div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Variance badge helper
// ─────────────────────────────────────────────────────────────────────────────

const Variance = ({ po, bill, isAmount = false, tolerance = 0.01 }) => {
  const p = parseFloat(po) || 0;
  const b = parseFloat(bill) || 0;
  const diff = b - p;
  if (Math.abs(diff) <= tolerance) {
    return <Text style={{ color: '#52c41a', fontSize: 12 }}>✓ match</Text>;
  }
  const sign = diff > 0 ? '+' : '';
  const label = isAmount ? `₹ ${fmtNum(Math.abs(diff))}` : fmtNum(Math.abs(diff), 4);
  return (
    <Text style={{ color: diff > 0 ? '#ff4d4f' : '#fa8c16', fontSize: 12 }}>
      {sign}{diff > 0 ? '+' : '−'}{label}
    </Text>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Per-item manual flag state
// LOCAL ONLY — stored in component state; tells you which items the reviewer
// has manually checked or flagged. The actual bill verification still goes via
// the existing PATCH /bills/:id/verify API.
// ─────────────────────────────────────────────────────────────────────────────

const FLAG = { NONE: 'none', OK: 'ok', FLAGGED: 'flagged' };

const FlagButton = ({ state, onChange }) => {
  if (state === FLAG.OK) {
    return (
      <Space size={4}>
        <Button
          size="small"
          type="primary"
          icon={<CheckOutlined />}
          style={{ background: '#52c41a', borderColor: '#52c41a', fontSize: 11 }}
          onClick={() => onChange(FLAG.NONE)}
        >
          OK
        </Button>
        <Button
          size="small"
          danger
          icon={<StopOutlined />}
          style={{ fontSize: 11 }}
          onClick={() => onChange(FLAG.FLAGGED)}
        />
      </Space>
    );
  }
  if (state === FLAG.FLAGGED) {
    return (
      <Space size={4}>
        <Button
          size="small"
          danger
          icon={<StopOutlined />}
          style={{ fontSize: 11 }}
          onClick={() => onChange(FLAG.NONE)}
        >
          Flagged
        </Button>
        <Button
          size="small"
          type="primary"
          icon={<CheckOutlined />}
          style={{ background: '#52c41a', borderColor: '#52c41a', fontSize: 11 }}
          onClick={() => onChange(FLAG.OK)}
        />
      </Space>
    );
  }
  // NONE
  return (
    <Space size={4}>
      <Tooltip title="Mark as OK">
        <Button
          size="small"
          icon={<CheckOutlined />}
          style={{ fontSize: 11 }}
          onClick={() => onChange(FLAG.OK)}
        />
      </Tooltip>
      <Tooltip title="Flag this item">
        <Button
          size="small"
          danger
          icon={<StopOutlined />}
          style={{ fontSize: 11 }}
          onClick={() => onChange(FLAG.FLAGGED)}
        />
      </Tooltip>
    </Space>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

/**
 * BillReceiptDetail
 *
 * Props:
 *  - bill    : KpBillReceipt object
 *  - loading : boolean indicating if full details are being fetched
 *  - onClose : callback to close parent modal + refresh list
 */
const BillReceiptDetail = ({ bill, loading, onClose }) => {
  const dispatch = useDispatch();
  const { actionLoading, poReconciliation, error: verificationError } = useSelector((s) => s.billReceipts);
  const { billPayments } = useSelector((s) => s.payments);
  const permissions = useSelector((s) => s.auth.permissions || []);

  const canVerify = hasPermission(permissions, 'bill_receipt/verify');
  const canReject = hasPermission(permissions, 'bill_receipt/reject');
  const canClose  = hasPermission(permissions, 'bill_receipt/close');

  // Clear any previous errors when component mounts or bill changes
  React.useEffect(() => {
    dispatch(clearBillError());
  }, [bill?.id, dispatch]);

  // Fetch PO reconciliation when component mounts to check if we can close
  React.useEffect(() => {
    if (bill && bill.purchase_order_id && bill.bill_status === 'verified') {
      dispatch(fetchPOReconciliationRequest(bill.purchase_order_id));
    }
  }, [bill, dispatch]);

  // Price anomaly state
  const [anomalies, setAnomalies] = React.useState([]);
  const [anomalyDismissed, setAnomalyDismissed] = React.useState(false);

  React.useEffect(() => {
    if (bill?.id) {
      intelligenceAPI.getBillAnomalies(bill.id)
        .then(res => setAnomalies((res.data?.anomalies || []).filter(a => a.is_anomaly)))
        .catch(() => {});
    }
  }, [bill?.id]);

  // Action remarks/reason state
  const [verifyRemarks, setVerifyRemarks] = useState('');
  const [closeRemarks,  setCloseRemarks]  = useState('');
  const [rejectReason,  setRejectReason]  = useState('');
  const [rejectError,   setRejectError]   = useState('');

  // Per-item manual review state  { [itemId]: FLAG.OK | FLAG.FLAGGED | FLAG.NONE }
  const [itemFlags, setItemFlags] = useState({});

  // NOTE: all hooks must be declared before any conditional return (Rules of Hooks)
  const items = bill ? (bill.items || []) : [];

  // ── Per-item flag helpers ────────────────────────────────────────────────
  const setFlag = (id, value) =>
    setItemFlags((prev) => ({ ...prev, [id]: value }));

  const flaggedCount = Object.values(itemFlags).filter((v) => v === FLAG.FLAGGED).length;
  const okCount      = Object.values(itemFlags).filter((v) => v === FLAG.OK).length;
  const reviewedPct  = items.length > 0 ? Math.round(((flaggedCount + okCount) / items.length) * 100) : 0;
  const allReviewed  = items.length > 0 && (flaggedCount + okCount) === items.length;

  // ── Action handlers ──────────────────────────────────────────────────────
  const handleVerify = () => {
    dispatch(verifyBillRequest(bill.id, { remarks: verifyRemarks }));
    // Don't close immediately - wait for success/error to display result
  };
  const handleReject = () => {
    if (!rejectReason.trim()) { setRejectError('Rejection reason is mandatory.'); return; }
    setRejectError('');
    dispatch(rejectBillRequest(bill.id, { rejection_reason: rejectReason }));
    // Close after dispatching reject action
    setTimeout(() => {
      if (onClose) onClose();
    }, 500);
  };
  const handleClose = () => {
    dispatch(closeBillRequest(bill.id, { remarks: closeRemarks }));
    // Close after dispatching close action
    setTimeout(() => {
      if (onClose) onClose();
    }, 500);
  };

  // Snapshot each bill's status as it was when its detail view was opened, so
  // the auto-close effect below can tell "just verified in this session"
  // apart from "was already verified before I opened it" (this modal has no
  // destroyOnClose, so this component persists across different bills).
  const openedBillStatusRef = React.useRef(bill?.bill_status);
  React.useEffect(() => {
    openedBillStatusRef.current = bill?.bill_status;
  }, [bill?.id]);

  // Auto-close modal on a verification performed in this session (after delay
  // to show success message) - guarded so simply viewing an already-verified
  // bill from the All Bills tab doesn't also trigger an auto-close.
  React.useEffect(() => {
    if (
      bill &&
      openedBillStatusRef.current === 'received' &&
      bill.bill_status === 'verified' &&
      !actionLoading &&
      !verificationError
    ) {
      const timer = setTimeout(() => {
        if (onClose) onClose();
      }, 1500); // Give user time to see success message
      return () => clearTimeout(timer);
    }
  }, [bill?.bill_status, actionLoading, verificationError, onClose]);

  // ── Computed totals from bill items ──────────────────────────────────────
  const totals = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        const a = computeAmounts(
          item.billed_quantity,
          item.billed_rate,
          item.discount,
          item.tax_slab
        );
        acc.subtotal    += a.subtotal;
        acc.discountAmt += a.discountAmt;
        acc.taxable     += a.taxable;
        acc.taxAmt      += a.taxAmt;
        acc.total       += a.total;
        return acc;
      },
      { subtotal: 0, discountAmt: 0, taxable: 0, taxAmt: 0, total: 0 }
    );
  }, [items]);

  // Block verification in the detail popup if any line-level or amount-level discrepancy exists.
  const verificationDiscrepancy = useMemo(() => {
    const summary = {
      qtyMismatchCount: 0,
      rateMismatchCount: 0,
      taxMismatchCount: 0,
      discountMismatchCount: 0,
      notInPoCount: 0,
      statusMismatchCount: 0,
      grandTotalMismatch: false,
    };

    items.forEach((item) => {
      const billedQty = parseFloat(item.billed_quantity) || 0;
      const billedRate = parseFloat(item.billed_rate) || 0;
      const billedDiscount = parseFloat(item.discount) || 0;

      // Items not linked to a PO are counted separately; skip individual field checks for them
      // because all fields default to 0/empty when po_item is not found, causing false positives.
      if (item.purchase_order_item_id == null || item.reconciliation_status === 'not_in_po') {
        summary.notInPoCount += 1;
      } else {
        // po_quantity=0 means the backend couldn't find the PO item (not a real zero-qty PO item)
        const hasPoQty = Number(item.po_quantity) > 0;
        const hasPoRate = item.po_rate != null && item.po_rate !== '';
        const hasPoTax = item.po_tax_slab != null && item.po_tax_slab !== '';
        const hasPoDiscount = item.po_discount != null && item.po_discount !== '';

        const poQty = parseFloat(item.po_quantity);
        const poRate = parseFloat(item.po_rate);
        const poDiscount = parseFloat(item.po_discount);
        const billTaxRate = parseFloat(item.tax_slab) || 0;
        const poTaxRate = parseFloat(item.po_tax_slab) || 0;

        if (hasPoQty && Math.abs(billedQty - (Number.isFinite(poQty) ? poQty : 0)) > 0.0001) {
          summary.qtyMismatchCount += 1;
        }

        if (hasPoRate && Math.abs(billedRate - (Number.isFinite(poRate) ? poRate : 0)) > 0.01) {
          summary.rateMismatchCount += 1;
        }

        // Compare tax rates numerically (matches backend parseTaxRate logic: '' and '0' both = 0%)
        if (hasPoTax && Math.abs(billTaxRate - poTaxRate) > 0.001) {
          summary.taxMismatchCount += 1;
        }

        if (hasPoDiscount && Math.abs(billedDiscount - (Number.isFinite(poDiscount) ? poDiscount : 0)) > 0.01) {
          summary.discountMismatchCount += 1;
        }
      }

      if (
        ['mismatched', 'rate_mismatch', 'qty_mismatch', 'tax_mismatch', 'discount_mismatch', 'not_in_po']
          .includes(item.reconciliation_status)
      ) {
        summary.statusMismatchCount += 1;
      }
    });

    const billAmount = parseFloat(bill?.bill_amount) || 0;
    // Use 1.0 tolerance to match the backend's ±1.00 allowance for rounding across multiple items
    summary.grandTotalMismatch = Math.abs((totals.total || 0) - billAmount) > 1.0;

    // statusMismatchCount is informational only (creation-time status, may be stale).
    // The backend re-runs reconciliation on verify and provides detailed errors.
    const hasAnyDiscrepancy =
      summary.qtyMismatchCount > 0 ||
      summary.rateMismatchCount > 0 ||
      summary.taxMismatchCount > 0 ||
      summary.discountMismatchCount > 0 ||
      summary.notInPoCount > 0 ||
      summary.grandTotalMismatch;

    return {
      ...summary,
      hasAnyDiscrepancy,
    };
  }, [items, totals.total, bill?.bill_amount]);

  // Now safe to early-return — all hooks have been called above
  if (loading && (!bill || !bill.items || !bill.items[0] || bill.items[0].po_quantity == null)) {
    return (
      <div style={{ padding: 60, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16, color: '#8c8c8c' }}>Loading bill details...</div>
      </div>
    );
  }

  if (!bill) return null;

  // ── Close-bill gating ────────────────────────────────────────────────────
  // Closing is the final, irreversible step, so it requires both the PO to
  // be fully reconciled AND the bill to be fully paid. Payment summary comes
  // from the same state.payments.billPayments cache PaymentPanel populates.
  const paymentSummary = billPayments[bill.id]?.summary;
  const poNotReconciled =
    poReconciliation && !['fully_received', 'verified', 'closed'].includes(poReconciliation.overall_status);
  // Treat "summary not loaded yet" as not-fully-paid so Close doesn't briefly
  // enable itself before the payment data has arrived.
  const paymentNotComplete = paymentSummary ? paymentSummary.payment_status !== 'fully_paid' : true;
  const closeBlocked = poNotReconciled || paymentNotComplete;
  const closeBlockedReason = poNotReconciled
    ? 'Cannot close until PO is fully matched'
    : paymentNotComplete
    ? 'Cannot close until the bill is fully paid'
    : '';

  // ── Timeline ─────────────────────────────────────────────────────────────
  const timelineItems = [];
  if (bill.received_at || bill.created_at) {
    timelineItems.push({
      color: 'blue',
      children: (
        <>
          <Text strong>Received</Text><br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {fmtDate(bill.received_at || bill.created_at)}
            {bill.received_by_name ? ` — ${bill.received_by_name}` : ''}
          </Text>
        </>
      ),
    });
  }
  if (bill.verified_at) {
    timelineItems.push({
      color: 'blue',
      dot: <CheckCircleOutlined style={{ color: '#1890ff' }} />,
      children: (
        <>
          <Text strong>Verified</Text><br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {fmtDate(bill.verified_at)}
            {bill.verified_by_name ? ` — ${bill.verified_by_name}` : ''}
          </Text>
        </>
      ),
    });
  }
  if (bill.rejected_at) {
    timelineItems.push({
      color: 'red',
      dot: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      children: (
        <>
          <Text strong style={{ color: '#ff4d4f' }}>Rejected</Text><br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {fmtDate(bill.rejected_at)}
            {bill.rejected_by_name ? ` — ${bill.rejected_by_name}` : ''}
          </Text>
          {bill.rejection_reason && (
            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 4, marginBottom: 0 }}>
              <WarningOutlined style={{ marginRight: 4 }} />
              {bill.rejection_reason}
            </Paragraph>
          )}
        </>
      ),
    });
  }
  if (bill.closed_at) {
    timelineItems.push({
      color: 'green',
      dot: <FileDoneOutlined style={{ color: '#52c41a' }} />,
      children: (
        <>
          <Text strong style={{ color: '#52c41a' }}>Closed</Text><br />
          <Text type="secondary" style={{ fontSize: 12 }}>{fmtDate(bill.closed_at)}</Text>
        </>
      ),
    });
  }

  // ── Challan links table ──────────────────────────────────────────────────
  const challanColumns = [
    {
      title: 'Challan #',
      key: 'challan_number',
      render: (_, r) => (
        <Text style={{ color: '#1890ff', fontWeight: 600 }}>
          {r.delivery_challan?.challan_number || `#${r.delivery_challan_id}`}
        </Text>
      ),
    },
    {
      title: 'Delivery Date',
      key: 'delivery_date',
      render: (_, r) => fmtShortDate(r.delivery_challan?.delivery_date),
    },
    {
      title: 'Delivery Status',
      key: 'delivery_status',
      render: (_, r) => {
        const s = r.delivery_challan?.delivery_status;
        const colorMap = { received: 'cyan', delivered: 'green', partial: 'orange' };
        return <Tag color={colorMap[s] || 'default'}>{s || '—'}</Tag>;
      },
    },
    {
      title: 'Bill Status',
      key: 'bill_status',
      render: (_, r) => getBillStatusTag(r.delivery_challan?.bill_status),
    },
    {
      title: 'Challan Amount (₹)',
      dataIndex: 'challan_amount',
      align: 'right',
      render: (v) => fmtMoney(v),
    },
  ];

  // ── Verification line-item comparison columns ─────────────────────────────
  //
  // Each row shows:  Product | PO Qty | Bill Qty | PO Rate | Bill Rate | Disc% | GST%
  //                 Subtotal | Disc Amt | Taxable | Tax Amt | Total | Δ vs PO | Flag
  //
  const verifyColumns = [
    {
      title: '#',
      key: 'idx',
      width: 36,
      fixed: 'left',
      render: (_, __, idx) => (
        <Text type="secondary" style={{ fontSize: 11 }}>{idx + 1}</Text>
      ),
    },
    {
      title: 'Product',
      dataIndex: 'product_name',
      key: 'product_name',
      width: 150,
      fixed: 'left',
      ellipsis: true,
      render: (name, record) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>{name || '—'}</Text>
          {record.unit && (
            <div style={{ fontSize: 11, color: '#999' }}>{record.unit}</div>
          )}
        </div>
      ),
    },
    // ── Quantity block ──
    {
      title: (
        <div className="verify-col-header">
          <div className="verify-col-po">PO Qty</div>
          <ArrowRightOutlined className="verify-col-arrow" />
          <div className="verify-col-bill">Bill Qty</div>
        </div>
      ),
      key: 'qty_block',
      width: 180,
      align: 'center',
      render: (_, record) => {
        const poQty   = record.po_quantity;
        const billQty = parseFloat(record.billed_quantity) || 0;
        return (
          <div className="verify-compare-cell">
            <span className="verify-po-val">{poQty != null ? fmtNum(poQty, 4) : <Text type="secondary">—</Text>}</span>
            <ArrowRightOutlined className="verify-arrow-icon" />
            <span className="verify-bill-val">{fmtNum(billQty, 4)}</span>
            <div className="verify-variance">
              {poQty != null
                ? <Variance po={poQty} bill={billQty} tolerance={0.0001} />
                : <Text type="secondary" style={{ fontSize: 11 }}>no PO data</Text>}
            </div>
          </div>
        );
      },
    },
    // ── Rate block ──
    {
      title: (
        <div className="verify-col-header">
          <div className="verify-col-po">PO Rate</div>
          <ArrowRightOutlined className="verify-col-arrow" />
          <div className="verify-col-bill">Bill Rate</div>
        </div>
      ),
      key: 'rate_block',
      width: 190,
      align: 'center',
      render: (_, record) => {
        const poRate   = record.po_rate;
        const billRate = parseFloat(record.billed_rate) || 0;
        return (
          <div className="verify-compare-cell">
            <span className="verify-po-val">{poRate != null ? `₹${fmtNum(poRate)}` : <Text type="secondary">—</Text>}</span>
            <ArrowRightOutlined className="verify-arrow-icon" />
            <span className="verify-bill-val">₹{fmtNum(billRate)}</span>
            <div className="verify-variance">
              {poRate != null
                ? <Variance po={poRate} bill={billRate} isAmount />
                : <Text type="secondary" style={{ fontSize: 11 }}>no PO data</Text>}
            </div>
          </div>
        );
      },
    },
    // ── Tax & Discount ──
    {
      title: 'Disc % / GST %',
      key: 'tax_disc',
      width: 120,
      align: 'center',
      render: (_, record) => {
        const poTax  = record.po_tax_slab;
        const poDisc = record.po_discount;
        return (
          <div style={{ fontSize: 12 }}>
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>Disc: </Text>
              <span className="verify-bill-val">{fmtNum(record.discount || 0, 2)}%</span>
              {poDisc != null && (
                <span style={{ marginLeft: 4 }}>
                  <Variance po={poDisc} bill={record.discount || 0} tolerance={0.01} />
                </span>
              )}
            </div>
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>GST: </Text>
              <span className="verify-bill-val">{record.tax_slab || 0}%</span>
              {poTax != null && String(poTax) !== String(record.tax_slab) && (
                <Tag color="purple" style={{ fontSize: 10, marginLeft: 4, padding: '0 4px' }}>
                  PO:{poTax}%
                </Tag>
              )}
            </div>
          </div>
        );
      },
    },
    // ── Computed amounts ──
    {
      title: 'Subtotal',
      key: 'subtotal',
      width: 110,
      align: 'right',
      render: (_, record) => {
        const a = computeAmounts(record.billed_quantity, record.billed_rate, record.discount, record.tax_slab);
        return <Text style={{ fontSize: 12 }}>₹{fmtNum(a.subtotal)}</Text>;
      },
    },
    {
      title: 'Discount ₹',
      key: 'disc_amt',
      width: 100,
      align: 'right',
      render: (_, record) => {
        const a = computeAmounts(record.billed_quantity, record.billed_rate, record.discount, record.tax_slab);
        return (
          <Text style={{ fontSize: 12, color: a.discountAmt > 0 ? '#52c41a' : '#bbb' }}>
            {a.discountAmt > 0 ? `−₹${fmtNum(a.discountAmt)}` : '—'}
          </Text>
        );
      },
    },
    {
      title: 'Taxable',
      key: 'taxable',
      width: 105,
      align: 'right',
      render: (_, record) => {
        const a = computeAmounts(record.billed_quantity, record.billed_rate, record.discount, record.tax_slab);
        return <Text style={{ fontSize: 12 }}>₹{fmtNum(a.taxable)}</Text>;
      },
    },
    {
      title: 'Tax ₹',
      key: 'tax_amt',
      width: 95,
      align: 'right',
      render: (_, record) => {
        const a = computeAmounts(record.billed_quantity, record.billed_rate, record.discount, record.tax_slab);
        return <Text style={{ fontSize: 12, color: '#fa8c16' }}>₹{fmtNum(a.taxAmt)}</Text>;
      },
    },
    {
      title: 'Line Total',
      key: 'line_total',
      width: 115,
      align: 'right',
      render: (_, record) => {
        const a = computeAmounts(record.billed_quantity, record.billed_rate, record.discount, record.tax_slab);
        return (
          <Text strong style={{ fontSize: 13, color: '#1890ff' }}>
            ₹{fmtNum(a.total)}
          </Text>
        );
      },
    },
    // ── Recon status (auto from backend) ──
    {
      title: 'Auto Recon',
      dataIndex: 'reconciliation_status',
      key: 'recon',
      width: 120,
      align: 'center',
      render: (status, record) => {
        if (!status || status === 'pending') {
          return <Tag color="default" style={{ fontSize: 11 }}>Pending</Tag>;
        }

        // Check if this is a critical error based on the note
        const note = record.reconciliation_note || '';
        const isCritical = note.includes('CRITICAL') ||
                          note.includes('OVERBILLED') ||
                          note.includes('overcharged') ||
                          note.includes('TAX MISMATCH') ||
                          note.includes('LESS discount');
        const isMinor = note.startsWith('Minor discrepancies (acceptable)');

        // If matched with a minor note, show as acceptable
        if (status === 'matched' && isMinor) {
          return (
            <Tooltip title={note}>
              <Tag color="blue" icon={<CheckCircleOutlined />} style={{ fontSize: 11 }}>
                Acceptable
              </Tag>
            </Tooltip>
          );
        }

        const map = {
          matched:           { color: 'success',  text: 'Matched', icon: null },
          mismatched:        { color: isCritical ? 'red' : 'orange',    text: isCritical ? 'BLOCKED' : 'Mismatch', icon: isCritical ? <CloseCircleOutlined /> : null },
          rate_mismatch:     { color: isCritical ? 'red' : 'orange',   text: isCritical ? 'Rate BLOCKED' : 'Rate ≠', icon: isCritical ? <CloseCircleOutlined /> : null },
          qty_mismatch:      { color: isCritical ? 'red' : 'orange',   text: isCritical ? 'Qty BLOCKED' : 'Qty ≠', icon: isCritical ? <CloseCircleOutlined /> : null },
          tax_mismatch:      { color: 'red',   text: 'Tax BLOCKED', icon: <CloseCircleOutlined /> },
          discount_mismatch: { color: isCritical ? 'red' : 'magenta',  text: isCritical ? 'Disc BLOCKED' : 'Disc ≠', icon: isCritical ? <CloseCircleOutlined /> : null },
          not_in_po:         { color: 'red',      text: 'Not in PO', icon: <CloseCircleOutlined /> },
        };
        const cfg = map[status] || { color: 'default', text: status, icon: null };

        return (
          <Tooltip title={note} overlayStyle={{ maxWidth: 400 }}>
            <Tag
              color={cfg.color}
              icon={cfg.icon}
              style={{
                fontSize: 11,
                fontWeight: isCritical ? 600 : 400
              }}
            >
              {cfg.text}
            </Tag>
          </Tooltip>
        );
      },
    },
    // ── Manual review flag ──
    {
      title: 'Review',
      key: 'review',
      width: 120,
      align: 'center',
      fixed: 'right',
      render: (_, record) => {
        const key = record.id ?? record.purchase_order_item_id ?? record.product_name;
        const state = itemFlags[key] ?? FLAG.NONE;
        return <FlagButton state={state} onChange={(v) => setFlag(key, v)} />;
      },
    },
  ];

  // ── Summary footer for the verification table ────────────────────────────
  const VerificationSummaryFooter = () => (
    <div className="verify-summary-footer">
      <Row gutter={[16, 8]} align="middle">
        <Col xs={24} sm={12}>
          <Space size={16} wrap>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Line Total (computed)</Text>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#1890ff' }}>
                ₹{fmtNum(totals.total)}
              </div>
            </div>
            <div className="verify-summary-breakdown">
              <div><Text type="secondary" style={{ fontSize: 11 }}>Subtotal: </Text><Text>₹{fmtNum(totals.subtotal)}</Text></div>
              <div><Text type="secondary" style={{ fontSize: 11 }}>Discount: </Text><Text style={{ color: '#52c41a' }}>−₹{fmtNum(totals.discountAmt)}</Text></div>
              <div><Text type="secondary" style={{ fontSize: 11 }}>Taxable: </Text><Text>₹{fmtNum(totals.taxable)}</Text></div>
              <div><Text type="secondary" style={{ fontSize: 11 }}>Tax/GST: </Text><Text style={{ color: '#fa8c16' }}>₹{fmtNum(totals.taxAmt)}</Text></div>
            </div>
          </Space>
        </Col>
        <Col xs={24} sm={12}>
          <div style={{ textAlign: 'right' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>vs. Bill Grand Total (vendor invoice)</Text>
            <div style={{ fontSize: 18, fontWeight: 700, color: bill.bill_amount > 0 && Math.abs(totals.total - bill.bill_amount) > 1 ? '#ff4d4f' : '#52c41a' }}>
              ₹{fmtNum(bill.bill_amount)}
            </div>
            {Math.abs(totals.total - bill.bill_amount) > 0.5 && (
              <Tag color="warning" style={{ fontSize: 11 }}>
                Δ ₹{fmtNum(Math.abs(totals.total - bill.bill_amount))} difference
              </Tag>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );

  // ── Review progress bar ──────────────────────────────────────────────────
  const ReviewProgress = () => {
    if (items.length === 0) return null;
    return (
      <div className="verify-review-progress">
        <Row align="middle" gutter={12}>
          <Col flex="auto">
            <Progress
              percent={reviewedPct}
              size="small"
              strokeColor={flaggedCount > 0 ? '#ff4d4f' : '#52c41a'}
              format={() => `${okCount + flaggedCount} / ${items.length} reviewed`}
            />
          </Col>
          <Col>
            <Space size={6}>
              {okCount > 0 && (
                <Tag color="success" style={{ fontSize: 11 }}>
                  <CheckOutlined /> {okCount} OK
                </Tag>
              )}
              {flaggedCount > 0 && (
                <Tag color="error" style={{ fontSize: 11 }}>
                  <StopOutlined /> {flaggedCount} Flagged
                </Tag>
              )}
              {items.length - okCount - flaggedCount > 0 && (
                <Tag color="default" style={{ fontSize: 11 }}>
                  <QuestionCircleOutlined /> {items.length - okCount - flaggedCount} Pending
                </Tag>
              )}
            </Space>
          </Col>
        </Row>
        {flaggedCount > 0 && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            message={`${flaggedCount} item(s) manually flagged by reviewer. Consider rejecting this bill.`}
            style={{ marginTop: 8, padding: '6px 12px' }}
          />
        )}
        {allReviewed && flaggedCount === 0 && (
          <Alert
            type="success"
            showIcon
            message="All items reviewed and marked OK. Ready to verify the bill."
            style={{ marginTop: 8, padding: '6px 12px' }}
          />
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="bill-detail-header">
        <div>
          <Title level={5} style={{ margin: 0 }}>
            {bill.bill_number || `Bill #${bill.id}`}
          </Title>
          <Space style={{ marginTop: 6 }} wrap>
            {getBillStatusTag(bill.bill_status)}
            {bill.reconciliation_status && bill.bill_status !== 'rejected' &&
              getReconStatusTag(bill.reconciliation_status)}
            {bill.attachment_url && (
              <a href={bill.attachment_url} target="_blank" rel="noreferrer">
                <Tag icon={<PaperClipOutlined />} color="default">Attachment</Tag>
              </a>
            )}
          </Space>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="bill-amount-large">{fmtMoney(bill.bill_amount)}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>Grand Total (vendor invoice)</Text>
        </div>
      </div>

      {/* ── Alerts ──────────────────────────────────────────────────────── */}

      {/* STRICT VERIFICATION ERROR - Show detailed error if verification failed */}
      {verificationError && verificationError.details && (
        <VerificationErrorAlert error={verificationError} />
      )}

      {/* PRE-VERIFICATION CHECK - Show issues before user attempts verification */}
      {bill.bill_status === 'received' && !verificationError && (
        <PreVerificationCheck bill={bill} />
      )}

      {bill.reconciliation_status === 'mismatched' && !verificationError && (
        <Alert
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          message="Auto-Reconciliation Mismatch"
          description={bill.reconciliation_remarks || 'One or more line items do not match the PO values.'}
          style={{ marginBottom: 12 }}
        />
      )}
      {bill.bill_status === 'rejected' && bill.rejection_reason && (
        <Alert
          type="error"
          showIcon
          icon={<CloseCircleOutlined />}
          message="Rejection Reason"
          description={bill.rejection_reason}
          style={{ marginBottom: 12 }}
        />
      )}

      <Divider style={{ margin: '10px 0' }} />

      {/* ── Price Anomaly Alert ──────────────────────────────────────────── */}
      {anomalies.length > 0 && !anomalyDismissed && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          closable
          onClose={() => setAnomalyDismissed(true)}
          style={{ marginBottom: 12 }}
          message={`Price Anomaly Detected — ${anomalies.length} item(s) priced significantly above historical average`}
          description={
            <div>
              {anomalies.map((a, i) => (
                <div key={i} style={{ fontSize: 12 }}>
                  <strong>{a.product_name}</strong>: Billed{' '}
                  <Text style={{ color: '#ff4d4f' }}>₹{Number(a.billed_rate).toFixed(2)}</Text>
                  {' '}vs avg ₹{Number(a.historical_avg_rate).toFixed(2)}
                  <Text style={{ color: '#ff4d4f', marginLeft: 4 }}>
                    (+{Number(a.deviation_pct).toFixed(1)}%)
                  </Text>
                </div>
              ))}
            </div>
          }
        />
      )}

      {/* ── Amount Summary Cards ─────────────────────────────────────────── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {[
          { title: 'Bill Amount',  value: bill.bill_amount,      color: '#1890ff' },
          { title: 'Taxable',      value: bill.taxable_amount,   color: '#595959' },
          { title: 'Tax / GST',    value: bill.tax_amount,       color: '#fa8c16' },
          { title: 'Discount',     value: bill.discount_amount,  color: '#52c41a' },
        ].map((s) => (
          <Col xs={12} sm={6} key={s.title}>
            <Card size="small" className="bill-amount-card">
              <Statistic
                title={s.title}
                value={fmtNum(s.value || 0)}
                prefix="₹"
                valueStyle={{ fontSize: 15, color: s.color }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* ── Bill + PO Info ───────────────────────────────────────────────── */}
      <div className="bill-detail-section">
        <SectionTitle>Bill & Purchase Order</SectionTitle>
        <Descriptions column={{ xs: 1, sm: 3 }} size="small" bordered>
          <Descriptions.Item label="Bill Number">
            <Text strong>{bill.bill_number}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Bill Date">
            {fmtShortDate(bill.bill_date)}
          </Descriptions.Item>
          <Descriptions.Item label="Vendor GST">
            {bill.vendor_gst_number || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="PO #">
            <Text strong>#{bill.purchase_order_id}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Vendor">
            {bill.purchase_order?.vendor_name || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="PO Total">
            <Text strong style={{ color: '#1890ff' }}>
              {fmtMoney(bill.purchase_order?.total_amount)}
            </Text>
          </Descriptions.Item>
          {bill.remarks && (
            <Descriptions.Item label="Remarks" span={3}>
              {bill.remarks}
            </Descriptions.Item>
          )}
        </Descriptions>
      </div>

      {/* ── Linked Challans ──────────────────────────────────────────────── */}
      {bill.challan_links?.length > 0 && (
        <div className="bill-detail-section">
          <SectionTitle>
            Linked Delivery Challans ({bill.challan_links.length})
          </SectionTitle>
          <Table
            columns={challanColumns}
            dataSource={bill.challan_links}
            rowKey={(r) => r.id ?? r.delivery_challan_id}
            pagination={false}
            size="small"
            scroll={{ x: 580 }}
          />
        </div>
      )}

      {/* ── Strict Verification Rules Info ────────────────────────────── */}
      {bill.bill_status === 'received' && items.length > 0 && (
        <StrictVerificationInfo />
      )}

      {/* ── Line-by-Line Verification Panel ─────────────────────────────── */}
      {items.length > 0 && (
        <div className="bill-detail-section">
          <SectionTitle>
            Line-by-Line Verification
            <Badge
              count={items.length}
              style={{ background: '#1890ff', marginLeft: 8 }}
              size="small"
            />
            {bill.bill_status === 'received' && (
              <Tag color="gold" style={{ marginLeft: 8, fontSize: 11 }}>
                <ClockCircleOutlined /> Awaiting Verification
              </Tag>
            )}
            {bill.bill_status === 'verified' && (
              <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>
                <CheckCircleOutlined /> Verified
              </Tag>
            )}
          </SectionTitle>

          {/* Tip for the reviewer */}
          {bill.bill_status === 'received' && (
            <Alert
              type="info"
              showIcon
              message="Review each line item below. Compare PO values (ordered) with Bill values (invoiced). Use the Review buttons to mark items as OK or Flag issues. Flagged items will guide your decision to verify or reject."
              style={{ marginBottom: 12, fontSize: 12 }}
              closable
            />
          )}

          {/* Review progress */}
          <ReviewProgress />

          {/* Main verification table */}
          <div style={{ marginTop: 12, overflowX: 'auto' }}>
            <table className="verify-table">
              <thead>
                <tr>
                  <th className="verify-th verify-th-fixed" style={{ width: 32 }}>#</th>
                  <th className="verify-th verify-th-fixed" style={{ width: 150 }}>Product / Unit</th>
                  {/* Quantity */}
                  <th className="verify-th verify-th-group" colSpan={3}>Quantity</th>
                  {/* Rate */}
                  <th className="verify-th verify-th-group" colSpan={3}>Rate (₹)</th>
                  {/* Tax & disc */}
                  <th className="verify-th" style={{ width: 70 }}>Disc %</th>
                  <th className="verify-th" style={{ width: 65 }}>GST %</th>
                  {/* Computed */}
                  <th className="verify-th" style={{ width: 105 }}>Subtotal</th>
                  <th className="verify-th" style={{ width: 90 }}>Disc ₹</th>
                  <th className="verify-th" style={{ width: 110 }}>Taxable</th>
                  <th className="verify-th" style={{ width: 90 }}>Tax ₹</th>
                  <th className="verify-th verify-th-total" style={{ width: 115 }}>Line Total</th>
                  {/* Auto recon */}
                  <th className="verify-th" style={{ width: 105 }}>Auto Recon</th>
                  {/* Manual flag */}
                  <th className="verify-th verify-th-fixed-right" style={{ width: 110 }}>Review</th>
                </tr>
                {/* Sub-headers for PO/Bill split columns */}
                <tr className="verify-subheader-row">
                  <th className="verify-th-sub"></th>
                  <th className="verify-th-sub"></th>
                  <th className="verify-th-sub verify-sub-po">PO</th>
                  <th className="verify-th-sub verify-sub-sep">→</th>
                  <th className="verify-th-sub verify-sub-bill">Bill</th>
                  <th className="verify-th-sub verify-sub-po">PO</th>
                  <th className="verify-th-sub verify-sub-sep">→</th>
                  <th className="verify-th-sub verify-sub-bill">Bill</th>
                  <th className="verify-th-sub"></th>
                  <th className="verify-th-sub"></th>
                  <th className="verify-th-sub"></th>
                  <th className="verify-th-sub"></th>
                  <th className="verify-th-sub"></th>
                  <th className="verify-th-sub"></th>
                  <th className="verify-th-sub"></th>
                  <th className="verify-th-sub"></th>
                  <th className="verify-th-sub"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const key     = item.id ?? item.purchase_order_item_id ?? item.product_name ?? idx;
                  const flagState = itemFlags[key] ?? FLAG.NONE;
                  const a       = computeAmounts(item.billed_quantity, item.billed_rate, item.discount, item.tax_slab);
                  const hasPoQty = item.po_quantity != null;
                  const hasPoRate= item.po_rate != null;
                  const qtyDiff  = hasPoQty  ? Math.abs(parseFloat(item.billed_quantity) - parseFloat(item.po_quantity))   > 0.0001 : false;
                  const rateDiff = hasPoRate ? Math.abs(parseFloat(item.billed_rate)     - parseFloat(item.po_rate))        > 0.01   : false;
                  const taxDiff  = item.po_tax_slab  != null ? String(item.po_tax_slab)  !== String(item.tax_slab)          : false;
                  const discDiff = item.po_discount  != null ? Math.abs(parseFloat(item.po_discount) - parseFloat(item.discount || 0)) > 0.01 : false;

                  const rowClass = [
                    'verify-tr',
                    flagState === FLAG.OK      ? 'verify-tr-ok'      : '',
                    flagState === FLAG.FLAGGED ? 'verify-tr-flagged'  : '',
                    (qtyDiff || rateDiff || taxDiff || discDiff) && flagState === FLAG.NONE ? 'verify-tr-warn' : '',
                  ].filter(Boolean).join(' ');

                  const reconMap = {
                    matched:           { color: '#52c41a', text: 'Matched' },
                    mismatched:        { color: '#ff4d4f', text: 'Mismatch' },
                    rate_mismatch:     { color: '#fa8c16', text: 'Rate ≠'  },
                    qty_mismatch:      { color: '#fa8c16', text: 'Qty ≠'   },
                    tax_mismatch:      { color: '#722ed1', text: 'Tax ≠'   },
                    discount_mismatch: { color: '#eb2f96', text: 'Disc ≠'  },
                    not_in_po:         { color: '#ff4d4f', text: 'N/A PO'  },
                    pending:           { color: '#bbb',    text: 'Pending' },
                  };
                  const rCfg = reconMap[item.reconciliation_status] || { color: '#bbb', text: '—' };

                  return (
                    <tr key={key} className={rowClass}>
                      {/* # */}
                      <td className="verify-td verify-td-center">
                        <Text type="secondary" style={{ fontSize: 11 }}>{idx + 1}</Text>
                      </td>
                      {/* Product */}
                      <td className="verify-td">
                        <Text strong style={{ fontSize: 12 }}>{item.product_name || '—'}</Text>
                        {item.unit && <div style={{ fontSize: 11, color: '#999' }}>{item.unit}</div>}
                      </td>
                      {/* PO Qty */}
                      <td className="verify-td verify-td-right verify-po-cell">
                        {hasPoQty ? fmtNum(item.po_quantity, 4) : <Text type="secondary">—</Text>}
                      </td>
                      {/* sep */}
                      <td className="verify-td verify-td-center" style={{ color: '#ccc', width: 16 }}>→</td>
                      {/* Bill Qty */}
                      <td className={`verify-td verify-td-right verify-bill-cell${qtyDiff ? ' verify-cell-diff' : ''}`}>
                        <strong>{fmtNum(item.billed_quantity, 4)}</strong>
                        {qtyDiff && (
                          <div style={{ fontSize: 10 }}>
                            <Variance po={item.po_quantity} bill={item.billed_quantity} tolerance={0.0001} />
                          </div>
                        )}
                      </td>
                      {/* PO Rate */}
                      <td className="verify-td verify-td-right verify-po-cell">
                        {hasPoRate ? `₹${fmtNum(item.po_rate)}` : <Text type="secondary">—</Text>}
                      </td>
                      {/* sep */}
                      <td className="verify-td verify-td-center" style={{ color: '#ccc', width: 16 }}>→</td>
                      {/* Bill Rate */}
                      <td className={`verify-td verify-td-right verify-bill-cell${rateDiff ? ' verify-cell-diff' : ''}`}>
                        <strong>₹{fmtNum(item.billed_rate)}</strong>
                        {rateDiff && (
                          <div style={{ fontSize: 10 }}>
                            <Variance po={item.po_rate} bill={item.billed_rate} isAmount />
                          </div>
                        )}
                      </td>
                      {/* Disc % */}
                      <td className={`verify-td verify-td-center${discDiff ? ' verify-cell-diff' : ''}`}>
                        <span style={{ fontSize: 12 }}>{fmtNum(item.discount || 0, 2)}%</span>
                        {item.po_discount != null && discDiff && (
                          <div style={{ fontSize: 10, color: '#fa8c16' }}>PO:{fmtNum(item.po_discount, 2)}%</div>
                        )}
                      </td>
                      {/* GST % */}
                      <td className={`verify-td verify-td-center${taxDiff ? ' verify-cell-diff' : ''}`}>
                        <span style={{ fontSize: 12 }}>{item.tax_slab || 0}%</span>
                        {taxDiff && (
                          <div style={{ fontSize: 10, color: '#722ed1' }}>PO:{item.po_tax_slab}%</div>
                        )}
                      </td>
                      {/* Subtotal */}
                      <td className="verify-td verify-td-right">
                        <span style={{ fontSize: 12 }}>₹{fmtNum(a.subtotal)}</span>
                      </td>
                      {/* Disc ₹ */}
                      <td className="verify-td verify-td-right">
                        <span style={{ fontSize: 12, color: a.discountAmt > 0 ? '#52c41a' : '#bbb' }}>
                          {a.discountAmt > 0 ? `−₹${fmtNum(a.discountAmt)}` : '—'}
                        </span>
                      </td>
                      {/* Taxable */}
                      <td className="verify-td verify-td-right">
                        <span style={{ fontSize: 12 }}>₹{fmtNum(a.taxable)}</span>
                      </td>
                      {/* Tax ₹ */}
                      <td className="verify-td verify-td-right">
                        <span style={{ fontSize: 12, color: '#fa8c16' }}>₹{fmtNum(a.taxAmt)}</span>
                      </td>
                      {/* Line Total */}
                      <td className="verify-td verify-td-right verify-td-total">
                        <strong style={{ fontSize: 13, color: '#1890ff' }}>₹{fmtNum(a.total)}</strong>
                      </td>
                      {/* Auto Recon */}
                      <td className="verify-td verify-td-center">
                        <Tooltip title={item.reconciliation_note || ''}>
                          <span style={{ fontSize: 11, color: rCfg.color, fontWeight: 600 }}>
                            {rCfg.text}
                          </span>
                        </Tooltip>
                      </td>
                      {/* Manual flag */}
                      <td className="verify-td verify-td-center">
                        <FlagButton
                          state={flagState}
                          onChange={(v) => setFlag(key, v)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Totals footer row */}
              <tfoot>
                <tr className="verify-tfoot-row">
                  <td colSpan={10} className="verify-td verify-tfoot-label">
                    <Text strong>Computed Totals (from line items)</Text>
                  </td>
                  <td className="verify-td verify-td-right verify-tfoot-cell">
                    <strong>₹{fmtNum(totals.subtotal)}</strong>
                  </td>
                  <td className="verify-td verify-td-right verify-tfoot-cell" style={{ color: '#52c41a' }}>
                    <strong>−₹{fmtNum(totals.discountAmt)}</strong>
                  </td>
                  <td className="verify-td verify-td-right verify-tfoot-cell">
                    <strong>₹{fmtNum(totals.taxable)}</strong>
                  </td>
                  <td className="verify-td verify-td-right verify-tfoot-cell" style={{ color: '#fa8c16' }}>
                    <strong>₹{fmtNum(totals.taxAmt)}</strong>
                  </td>
                  <td className="verify-td verify-td-right verify-tfoot-cell verify-tfoot-total">
                    <strong style={{ color: '#1890ff', fontSize: 14 }}>₹{fmtNum(totals.total)}</strong>
                  </td>
                  <td colSpan={2} className="verify-td"></td>
                </tr>
                {/* Grand total comparison */}
                <tr className="verify-tfoot-compare-row">
                  <td colSpan={14} className="verify-td verify-tfoot-label">
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Vendor invoice grand total:
                    </Text>
                    <Text strong style={{ marginLeft: 8, fontSize: 14, color: '#1a1a2e' }}>
                      ₹{fmtNum(bill.bill_amount)}
                    </Text>
                    {Math.abs(totals.total - (bill.bill_amount || 0)) > 0.5 ? (
                      <Tag color="warning" style={{ marginLeft: 12, fontSize: 11 }}>
                        Δ ₹{fmtNum(Math.abs(totals.total - (bill.bill_amount || 0)))} difference
                      </Tag>
                    ) : (
                      <Tag color="success" style={{ marginLeft: 12, fontSize: 11 }}>
                        ✓ Matches invoice total
                      </Tag>
                    )}
                  </td>
                  <td colSpan={3} className="verify-td"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ── Payment Tracking ─────────────────────────────────────────────── */}
      {bill.bill_status !== 'rejected' && (
        <div className="bill-detail-section">
          <SectionTitle>Payments</SectionTitle>
          <PaymentPanel bill={bill} />
        </div>
      )}

      {/* ── Audit Trail ──────────────────────────────────────────────────── */}
      {timelineItems.length > 0 && (
        <div className="bill-detail-section">
          <SectionTitle>Audit Trail</SectionTitle>
          <Timeline items={timelineItems} style={{ paddingTop: 8 }} />
        </div>
      )}

      {/* ── Timestamps ───────────────────────────────────────────────────── */}
      <div className="bill-detail-section">
        <SectionTitle>Timestamps</SectionTitle>
        <Descriptions column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label="Created">{fmtDate(bill.created_at)}</Descriptions.Item>
          <Descriptions.Item label="Last Updated">{fmtDate(bill.updated_at)}</Descriptions.Item>
        </Descriptions>
      </div>

      {/* ── Actions ──────────────────────────────────────────────────────── */}
      {bill.bill_status !== 'closed' && (canVerify || canReject || canClose) && (
        <>
          <Divider style={{ margin: '8px 0 16px' }} />
          <div className="bill-detail-section">
            <SectionTitle>Actions</SectionTitle>

            {/* VERIFY */}
            {canVerify && bill.bill_status === 'received' && (
              <div className="bill-action-block">
                <Text type="secondary" className="bill-action-description">
                  Verify this bill (received → verified). Triggers auto line-item reconciliation against PO.
                  {flaggedCount > 0 && (
                    <Text type="danger" style={{ marginLeft: 8 }}>
                      ⚠ {flaggedCount} item(s) flagged — consider rejecting instead.
                    </Text>
                  )}
                </Text>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Input.TextArea
                    rows={2}
                    placeholder="Optional verification remarks…"
                    value={verifyRemarks}
                    onChange={(e) => setVerifyRemarks(e.target.value)}
                    maxLength={500}
                    showCount
                  />
                  {verificationDiscrepancy.hasAnyDiscrepancy ? (
                    <Alert
                      type="error"
                      showIcon
                      icon={<ExclamationCircleOutlined />}
                      message="Verification blocked due to discrepancies"
                      description={(
                        <div>
                          {verificationDiscrepancy.grandTotalMismatch && (
                            <div>- Invoice grand total does not match computed line-item total.</div>
                          )}
                          {verificationDiscrepancy.qtyMismatchCount > 0 && (
                            <div>- {verificationDiscrepancy.qtyMismatchCount} line(s) have quantity mismatch.</div>
                          )}
                          {verificationDiscrepancy.rateMismatchCount > 0 && (
                            <div>- {verificationDiscrepancy.rateMismatchCount} line(s) have rate mismatch.</div>
                          )}
                          {verificationDiscrepancy.taxMismatchCount > 0 && (
                            <div>- {verificationDiscrepancy.taxMismatchCount} line(s) have tax slab mismatch.</div>
                          )}
                          {verificationDiscrepancy.discountMismatchCount > 0 && (
                            <div>- {verificationDiscrepancy.discountMismatchCount} line(s) have discount mismatch.</div>
                          )}
                          {verificationDiscrepancy.notInPoCount > 0 && (
                            <div>- {verificationDiscrepancy.notInPoCount} line(s) are not linked to a PO item.</div>
                          )}
                          {!verificationDiscrepancy.grandTotalMismatch &&
                            verificationDiscrepancy.qtyMismatchCount === 0 &&
                            verificationDiscrepancy.rateMismatchCount === 0 &&
                            verificationDiscrepancy.taxMismatchCount === 0 &&
                            verificationDiscrepancy.discountMismatchCount === 0 &&
                            verificationDiscrepancy.notInPoCount === 0 &&
                            verificationDiscrepancy.statusMismatchCount > 0 && (
                              <div>- Reconciliation status indicates mismatch on one or more line items.</div>
                            )}
                        </div>
                      )}
                    />
                  ) : (
                    <Popconfirm
                      title="Verify this bill?"
                      description="Moves status: received → verified. Reconciliation will run automatically."
                      onConfirm={handleVerify}
                      okText="Yes, Verify"
                      cancelText="Cancel"
                    >
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        loading={actionLoading}
                        disabled={flaggedCount > 0}
                      >
                        Verify Bill
                        {allReviewed && flaggedCount === 0 && items.length > 0 && (
                          <Tag color="success" style={{ marginLeft: 8, fontSize: 10 }}>All OK</Tag>
                        )}
                      </Button>
                    </Popconfirm>
                  )}
                  {flaggedCount > 0 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Clear all flagged items or use Reject to send back to vendor.
                    </Text>
                  )}
                </Space>
              </div>
            )}

            {/* CLOSE */}
            {canClose && bill.bill_status === 'verified' && (
              <div className="bill-action-block">
                <Text type="secondary" className="bill-action-description">
                  Close this bill (verified → closed). Final, irreversible step.
                  <br />
                  <br />
                  <strong>Note:</strong> A bill can only be closed once the entire Purchase Order is fully reconciled (all quantities matched and billed) <strong>and</strong> the bill is fully paid.
                </Text>

                {poNotReconciled && (
                  <Alert
                    type="error"
                    showIcon
                    icon={<ExclamationCircleOutlined />}
                    message="Purchase Order Not Fully Reconciled"
                    description="You cannot close this bill yet. The overall Purchase Order is not completely fulfilled and matched. Wait for all pending challans to be billed or fix any mismatches."
                    style={{ marginBottom: 12, marginTop: 12 }}
                  />
                )}

                {paymentNotComplete && (
                  <Alert
                    type="error"
                    showIcon
                    icon={<ExclamationCircleOutlined />}
                    message="Bill Not Fully Paid"
                    description={
                      paymentSummary
                        ? `You cannot close this bill yet. ₹${fmtNum(paymentSummary.outstanding)} is still outstanding (₹${fmtNum(paymentSummary.total_paid)} paid of ₹${fmtNum(paymentSummary.bill_amount)}). Record the remaining payment in the Payments section below.`
                        : 'Checking payment status…'
                    }
                    style={{ marginBottom: 12, marginTop: 12 }}
                  />
                )}

                <Space direction="vertical" style={{ width: '100%' }}>
                  <Input.TextArea
                    rows={2}
                    placeholder="Optional closing remarks… (e.g. Payment processed via NEFT)"
                    value={closeRemarks}
                    onChange={(e) => setCloseRemarks(e.target.value)}
                    maxLength={500}
                    showCount
                    disabled={closeBlocked}
                  />
                  <Popconfirm
                    title="Close this bill?"
                    description="Moves the bill to 'closed'. Cannot be undone."
                    onConfirm={handleClose}
                    okText="Yes, Close"
                    cancelText="Cancel"
                    okType="danger"
                    disabled={closeBlocked}
                  >
                    <Tooltip title={closeBlockedReason}>
                      <Button
                        type="primary"
                        danger
                        icon={<FileDoneOutlined />}
                        loading={actionLoading}
                        disabled={closeBlocked}
                      >
                        Close Bill
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                </Space>
              </div>
            )}

            {/* REJECT */}
            {canReject && (bill.bill_status === 'received' || bill.bill_status === 'verified') && (
              <div className="bill-action-block bill-action-block--danger">
                <Text type="secondary" className="bill-action-description">
                  Reject this bill. All linked challans revert to <Text code>pending</Text> so a corrected bill can be submitted.
                </Text>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Input.TextArea
                    rows={3}
                    placeholder="Rejection reason (mandatory — describe the discrepancy)…"
                    value={rejectReason}
                    onChange={(e) => { setRejectReason(e.target.value); if (e.target.value.trim()) setRejectError(''); }}
                    maxLength={1000}
                    showCount
                    status={rejectError ? 'error' : ''}
                  />
                  {rejectError && <Text type="danger" style={{ fontSize: 12 }}>{rejectError}</Text>}
                  <Popconfirm
                    title="Reject this bill?"
                    description="Linked challans revert to 'pending'. A corrected bill may then be submitted."
                    onConfirm={handleReject}
                    okText="Yes, Reject"
                    cancelText="Cancel"
                    okType="danger"
                  >
                    <Button danger icon={<CloseCircleOutlined />} loading={actionLoading}>
                      Reject Bill
                    </Button>
                  </Popconfirm>
                </Space>
              </div>
            )}

            {/* State notices */}
            {bill.bill_status === 'closed' && (
              <Alert type="success" showIcon icon={<FileDoneOutlined />}
                message="This bill is fully closed. No further actions are available." />
            )}
            {bill.bill_status === 'rejected' && (
              <Alert type="error" showIcon icon={<CloseCircleOutlined />}
                message="This bill has been rejected."
                description="Linked challans reverted to 'pending'. Submit a corrected bill against those challans." />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BillReceiptDetail;
